import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import BackToResultsButton from '../components/BackToResultsButton';
import ShareModal from '../components/ShareModal';
import { hasNavigationState } from '../utils/navigationState';
import { 
  Building, 
  Globe, 
  Linkedin, 
  MapPin, 
  FileText, 
  Award,
  ExternalLink,
  ArrowLeft,
  Phone,
  Mail,
  Briefcase,
  Share2,
  Star,
  Heart,
  Users,
  Handshake,
  MessageCircle,
  Calendar,
  Video,
  PhoneCall
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyProfilePage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminContact, setAdminContact] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [stats, setStats] = useState(null);
  const [enrichedStats, setEnrichedStats] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(true);

  useEffect(() => {
    fetchCompanyProfile();
    checkIfFavorited();
  }, [companyId]);

  const fetchCompanyProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch company data, stats, enriched stats, ratings and contact in parallel
      const [companyRes, statsRes, enrichedStatsRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/companies/${companyId}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })), // Stats are optional
        axios.get(`${API}/companies/${companyId}/enriched-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })), // Enriched stats are optional
        axios.get(`${API}/ratings/company/${companyId}`).catch(() => ({ data: null })) // Ratings are optional
      ]);
      
      setCompany(companyRes.data);
      setStats(statsRes.data);
      setEnrichedStats(enrichedStatsRes.data);
      setRatings(ratingsRes.data);
      
      // Always try to fetch admin contact - backend will check if authorized
      await fetchAdminContact();
    } catch (error) {
      console.error('Failed to fetch company profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminContact = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/companies/${companyId}/admin-contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminContact(response.data);
      setShowContact(true);
    } catch (error) {
      // 403 means no accepted reservation exists - this is expected
      if (error.response?.status === 403) {
        console.log('Contact information not available - no accepted reservation');
      } else {
        console.error('Failed to fetch admin contact:', error);
      }
      setShowContact(false);
    }
  };

  const checkIfFavorited = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/favorites/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const favorited = response.data.some(fav => 
        (fav.favorite?.favorited_company_id || fav.company?.id) === companyId
      );
      setIsFavorited(favorited);
    } catch (error) {
      console.error('Failed to check favorite status');
    } finally {
      setCheckingFavorite(false);
    }
  };

  const handleToggleFavorite = async () => {
    const token = localStorage.getItem('token');
    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/company/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorited(false);
      } else {
        await axios.post(`${API}/favorites/company/${companyId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorited(true);
      }
    } catch (error) {
      console.error('Failed to update favorites');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company profile...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">Company not found</p>
            <Button onClick={() => navigate(-1)} className="w-full mt-4">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Back Button - Smart navigation with state restoration or fallback to browser back */}
        <BackToResultsButton />
        {/* Fallback if no saved state - regular back button */}
        {!hasNavigationState() && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        {/* Company Header */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              {/* Logo */}
              {company.logo_url && (
                <div className="flex-shrink-0">
                  <img
                    src={`${BACKEND_URL}${company.logo_url}`}
                    alt={company.name}
                    className="w-24 h-24 object-contain rounded-lg border border-gray-200"
                  />
                </div>
              )}

              {/* Company Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2" style={{ color: '#1B3240' }}>
                  {company.name}
                </h1>
                
                <div className="flex flex-wrap gap-3 mb-4">
                  {company.company_type && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {company.company_type === 'partner' && 'ServiceNow Partner'}
                      {company.company_type === 'consulting' && 'Consulting Firm'}
                      {company.company_type === 'recruitment' && 'Recruitment Agency'}
                      {company.company_type === 'servicenow_editor' && 'ServiceNow Editor/ISV'}
                      {company.company_type === 'other' && (company.company_type_detail || 'Other')}
                    </Badge>
                  )}
                  
                  {company.focus_specialization && (
                    <Badge style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
                      {company.focus_specialization}
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap gap-4">
                  {company.website_url && (
                    <a
                      href={company.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                    >
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">Website</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  {company.linkedin_url && (
                    <a
                      href={company.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                    >
                      <Linkedin className="w-4 h-4" />
                      <span className="text-sm">LinkedIn</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex-shrink-0 flex gap-2">
                <Button
                  variant="ghost"
                  onClick={handleToggleFavorite}
                  disabled={checkingFavorite}
                  title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart className={`w-5 h-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="md:col-span-2 space-y-6">
            {/* Description */}
            {company.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    About
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {company.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Projects/Resources Stats */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Briefcase className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    {stats.type === 'PM' ? 'Projects Portfolio' : 'Resources Pool'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stats Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Total</p>
                      <p className="text-3xl font-bold" style={{ color: '#1B3240' }}>{stats.total}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {stats.type === 'PM' ? 'Projects created' : 'Resources registered'}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Active Now</p>
                      <p className="text-3xl font-bold" style={{ color: '#62D84E' }}>{stats.active}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {stats.type === 'PM' ? 'Active projects' : 'Available resources'}
                      </p>
                    </div>
                  </div>

                  {/* List of active items */}
                  {stats.items && stats.items.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        {stats.type === 'PM' ? 'Recent Active Projects' : 'Available Resources'}
                      </p>
                      <div className="space-y-2">
                        {stats.items.slice(0, 5).map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              if (stats.type === 'PM') {
                                navigate(`/projects/${item.id}`);
                              } else {
                                navigate(`/resources/${item.id}`);
                              }
                            }}
                            className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {stats.type === 'PM' ? (
                                  <>
                                    <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {item.role} - {item.seniority}
                                    </p>
                                    {item.start_date && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Start: {new Date(item.start_date).toLocaleDateString()}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-900 text-sm">
                                        {item.role} - {item.seniority}
                                      </p>
                                      {item.is_available && (
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                                          Available
                                        </span>
                                      )}
                                    </div>
                                    {item.skills && item.skills.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {item.skills.slice(0, 3).map((skill, idx) => (
                                          <span
                                            key={idx}
                                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded"
                                          >
                                            {skill}
                                          </span>
                                        ))}
                                        {item.skills.length > 3 && (
                                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                            +{item.skills.length - 3} more
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                              <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                            </div>
                          </button>
                        ))}
                      </div>
                      {stats.items.length > 5 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          +{stats.items.length - 5} more {stats.type === 'PM' ? 'projects' : 'resources'}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Contact Company Button - MOVED TO SIDEBAR */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-800">
                  <Mail className="w-5 h-5 mr-2" />
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-700 mb-4">
                  Interested in working with {company.name}? Send them a message.
                </p>
                <Button
                  onClick={() => navigate(`/messages?company=${companyId}&companyName=${encodeURIComponent(company.name)}`)}
                  className="w-full btn-snow px-2 sm:px-4 py-2"
                >
                  <Mail className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="hidden xs:inline sm:inline">Contact Company</span>
                  <span className="inline xs:hidden sm:hidden">Contact</span>
                </Button>
              </CardContent>
            </Card>

            {/* ServiceNow Partner Info - MOVED TO SIDEBAR */}
            {company.company_type === 'partner' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    ServiceNow Partner Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {company.partner_level && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Partner Level</p>
                      <Badge className="bg-purple-100 text-purple-700">
                        {company.partner_level}
                      </Badge>
                    </div>
                  )}

                  {company.csat_score && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">ServiceNow CSAT Score</p>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold" style={{ color: '#62D84E' }}>
                          {company.csat_score}
                        </span>
                        <span className="text-gray-600">out of 5</span>
                      </div>
                      {company.csat_date && (
                        <p className="text-xs text-gray-500 mt-1">
                          As of {new Date(company.csat_date + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                        </p>
                      )}
                    </div>
                  )}

                  {company.partner_finder_url && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Partner Finder</p>
                      <a
                        href={company.partner_finder_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
                      >
                        View on ServiceNow Partner Finder
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* NEW: Collaborations Block */}
            {enrichedStats && (enrichedStats.collaborations?.active > 0 || enrichedStats.collaborations?.past > 0) && (
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-indigo-800">
                    <Handshake className="w-5 h-5 mr-2" />
                    Collaborations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-indigo-700">{enrichedStats.collaborations?.active || 0}</p>
                      <p className="text-xs text-indigo-600">Active</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">{enrichedStats.collaborations?.past || 0}</p>
                      <p className="text-xs text-purple-600">Completed</p>
                    </div>
                  </div>
                  {enrichedStats.collaborations?.interviews_completed > 0 && (
                    <div className="mt-3 text-center text-sm text-indigo-600">
                      <Video className="w-4 h-4 inline mr-1" />
                      {enrichedStats.collaborations.interviews_completed} interviews conducted
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* NEW: Favorites Received Block (only if > 0) */}
            {enrichedStats && enrichedStats.favorites_received > 0 && (
              <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-pink-800">
                    <Heart className="w-5 h-5 mr-2 fill-pink-500" />
                    Trusted Partner
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-3xl font-bold text-pink-700">{enrichedStats.favorites_received}</p>
                  <p className="text-sm text-pink-600">
                    {enrichedStats.favorites_received === 1 ? 'company' : 'companies'} saved this company as favorite
                  </p>
                </CardContent>
              </Card>
            )}

            {/* NEW: Team Block */}
            {enrichedStats && enrichedStats.team?.total > 0 && (
              <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-cyan-800">
                    <Users className="w-5 h-5 mr-2" />
                    Team
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-3">
                    <p className="text-3xl font-bold text-cyan-700">{enrichedStats.team.total}</p>
                    <p className="text-sm text-cyan-600">
                      team member{enrichedStats.team.total > 1 ? 's' : ''}
                    </p>
                  </div>
                  {enrichedStats.team.members && enrichedStats.team.members.length > 0 && (
                    <div className="space-y-2">
                      {enrichedStats.team.members.slice(0, 3).map((member, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/60 rounded-lg p-2">
                          <div className="w-8 h-8 rounded-full bg-cyan-200 flex items-center justify-center text-cyan-700 font-medium text-sm">
                            {(member.first_name?.[0] || '') + (member.last_name?.[0] || '')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {member.is_admin ? 'Admin' : 'Member'}
                            </p>
                          </div>
                        </div>
                      ))}
                      {enrichedStats.team.total > 3 && (
                        <p className="text-xs text-cyan-600 text-center">
                          +{enrichedStats.team.total - 3} more members
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* NEW: Communication Preferences Block */}
            {enrichedStats && enrichedStats.communication?.preferred_methods?.length > 0 && (
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-amber-800">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Communication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-amber-600 mb-3">Preferred interview methods</p>
                  <div className="space-y-2">
                    {enrichedStats.communication.preferred_methods.map((method, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/60 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          {method.method?.toLowerCase().includes('video') || method.method?.toLowerCase().includes('visio') ? (
                            <Video className="w-4 h-4 text-amber-600" />
                          ) : method.method?.toLowerCase().includes('phone') || method.method?.toLowerCase().includes('call') ? (
                            <PhoneCall className="w-4 h-4 text-amber-600" />
                          ) : (
                            <MessageCircle className="w-4 h-4 text-amber-600" />
                          )}
                          <span className="text-sm text-gray-700 capitalize">{method.method}</span>
                        </div>
                        <span className="text-xs text-amber-600">{method.count}x</span>
                      </div>
                    ))}
                  </div>
                  {/* Calendar integrations */}
                  {(enrichedStats.communication.calendar_integrations?.google_calendar || 
                    enrichedStats.communication.calendar_integrations?.microsoft_outlook) && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-amber-600 mb-2">Calendar integrations</p>
                      <div className="flex gap-2">
                        {enrichedStats.communication.calendar_integrations.google_calendar && (
                          <Badge className="bg-white text-amber-700 text-xs">
                            <Calendar className="w-3 h-3 mr-1" />
                            Google
                          </Badge>
                        )}
                        {enrichedStats.communication.calendar_integrations.microsoft_outlook && (
                          <Badge className="bg-white text-amber-700 text-xs">
                            <Calendar className="w-3 h-3 mr-1" />
                            Outlook
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Ratings Section */}
            {ratings && ratings.total_ratings > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-yellow-800">
                    <Star className="w-5 h-5 mr-2 fill-yellow-500 text-yellow-500" />
                    Company Rating
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-center">
                      <span className="text-4xl font-bold text-yellow-700">
                        {ratings.average_score.toFixed(1)}
                      </span>
                      <span className="text-lg text-yellow-600">/5</span>
                    </div>
                    <div className="flex flex-col">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= Math.round(ratings.average_score)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-yellow-700 mt-1">
                        {ratings.total_ratings} rating{ratings.total_ratings > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Recent Reviews */}
                  {ratings.ratings && ratings.ratings.length > 0 && (
                    <div className="space-y-3 border-t border-yellow-200 pt-3">
                      <p className="text-sm font-medium text-yellow-800">Recent Reviews</p>
                      {ratings.ratings.slice(0, 3).map((rating, idx) => (
                        <div key={idx} className="bg-white rounded p-3 border border-yellow-100">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-3 h-3 ${
                                    star <= rating.score
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(rating.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {rating.comment && (
                            <p className="text-sm text-gray-700 italic">"{rating.comment}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Company Registration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Company Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.registration_number && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Registration Number</p>
                    <p className="text-gray-900">{company.registration_number}</p>
                  </div>
                )}

                {company.vat_number && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">VAT Number</p>
                    <p className="text-gray-900">{company.vat_number}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Office Address */}
            {(company.office_street || company.office_city) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <MapPin className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    Office Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-gray-700">
                    {company.office_street && <p>{company.office_street}</p>}
                    {company.office_city && (
                      <p>
                        {company.office_postal_code && `${company.office_postal_code} `}
                        {company.office_city}
                      </p>
                    )}
                    {company.office_country && <p>{company.office_country}</p>}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        url={window.location.href}
        title={`Company Profile - ${company?.name || 'Company'}`}
        description={company?.description}
      />
    </div>
  );
};

export default CompanyProfilePage;
